import { Component, Vue, Prop } from 'vue-property-decorator';
import { QuestionnaireDTO } from '@/models/QuestionnaireDTO';
import { AnswerChoices } from '@/models/VariousEnum';
import LastMammoPart from '@/components/Questionnaire/Health/LastMammoPart/LastMammoPart.vue';
import HormonalTreatmentPart from '@/components/Questionnaire/Health/HormonalTreatmentPart/HormonalTreatmentPart.vue';
import FamilyCancerPart from '@/components/Questionnaire/Health/FamilyCancerPart/FamilyCancerPart.vue';
import BreastSymptomsPart from '@/components/Questionnaire/Health/BreastSymptomsPart/BreastSymptomsPart.vue';
import BreastOperationsPart from '@/components/Questionnaire/Health/BreastOperationsPart/BreastOperationsPart.vue';
import { QuestQuestion } from '@/models/QuestQuestion';
import StepStatus from '@/components/StepStatus/StepStatus.vue';
import LogoutButton from '@/components/LogoutButton/LogoutButton.vue';

@Component({
  components: {
    LastMammoPart,
    HormonalTreatmentPart,
    FamilyCancerPart,
    BreastSymptomsPart,
    BreastOperationsPart,
    StepStatus,
    LogoutButton,
  },
})
export default class QuestQuestions extends Vue {
  @Prop({ required: false, default: false }) public isRecap!: boolean;
  public partId: number | undefined;
  public questionnaire: QuestionnaireDTO = {} as QuestionnaireDTO;
  public questions: QuestQuestion = {} as QuestQuestion;
  public answerChoices: any = AnswerChoices;
  public updating?: boolean = false;
  public errorMessage: string = '';
  public error: boolean = false;

  private hormonalTreatmentSinceValidity: boolean = true;
  private hormonalTreatmentBeforeNotSinceValidity: boolean = true;
  private hormonalTreatmentBeforeDurationValidity: boolean = true;

  protected mounted() {
    this.partId = Number(this.$route.params.partId);
    this.questionnaire = this.$store.state.questionnaire;
    this.questions = new QuestQuestion(this.questionnaire.questions);
    this.$store.commit('setQuestionnaireQuestions', (this.questionnaire.questions));
  }

  protected isLastMammoDateValid(): boolean {
    if (this.questionnaire.questions.flagLastMammo === AnswerChoices.Yes) {
      const today = new Date();
      const minDate = this.questionnaire.birthdate !== null ? this.questionnaire.birthdate : new Date(today.getFullYear() - 100, today.getMonth(), today.getDate());
      const lastMammoDate = this.questionnaire.questions.lastMammoDate;
      if (lastMammoDate !== null && minDate !== null && (lastMammoDate > today || lastMammoDate < minDate)) {
        return false;
      } else if (lastMammoDate !== null && lastMammoDate <= today) {
        return true;
      }
    }
    return true;
  }

  protected isHormonalTreatmentSinceValid(): boolean {
    if (this.questionnaire.questions.flagHormonalTreatment === AnswerChoices.Yes) {
      return this.hormonalTreatmentSinceValidity;
    }
    return true;
  }

  protected isHormonalTreatmentBeforeNotSinceValid(): boolean {
    if (this.questionnaire.questions.flagHormonalTreatment === AnswerChoices.No &&
      this.questionnaire.questions.flagHormonalTreatmentBefore === AnswerChoices.BeforeHT_NoSince) {
      return this.hormonalTreatmentBeforeNotSinceValidity;
    }
    return true;
  }

  protected isHormonalTreatmentBeforeDurationValid(): boolean {
    if (this.questionnaire.questions.flagHormonalTreatment === AnswerChoices.No &&
      this.questionnaire.questions.flagHormonalTreatmentBefore === AnswerChoices.BeforeHT_NoSince) {
      return this.hormonalTreatmentBeforeDurationValidity;
    }
    return true;
  }

  protected async onSubmit(event: any) {
    this.$store.commit('setQuestionnaireQuestions', this.questionnaire.questions);
    if (event.submitter.id === 'btn-continue') {
      this.$router.push({
        name: 'atHomeRecap',
        params: {
          screening: this.$route.params.screening,
          partId: String(this.partId),
        },
      });
    } else {
      this.$router.push({
        name: 'atHomeDoctor',
        params: {
          screening: this.$route.params.screening,
          partId: String(this.partId),
        },
      });
    }
  }

  protected onLastMammoUpdate(): void {
    if (this.questions.lastMammo) {
      Object.assign(this.questionnaire.questions, this.questions.lastMammo.toDto());
    }
  }

  protected onHormonalTreatmentUpdate(): void {
    if (this.questions.hormonalTreatment) {
      Object.assign(this.questionnaire.questions, this.questions.hormonalTreatment.toDto());
    }
  }

  /**
   * Set the hormonalTreatmentSinceValidity based on the received event value
   * @param validity The validity of the input
   */
  protected onHormonalTreatmentSinceValidityCheck(validity: boolean): void {
    this.hormonalTreatmentSinceValidity = validity;
  }

  /**
   * Set the hormonalTreatmentBeforeNotSinceValidity based on the received event value
   * @param validity The validity of the input
   */
  protected onHormonalTreatmentBeforeNotSinceValidityCheck(validity: boolean): void {
    this.hormonalTreatmentBeforeNotSinceValidity = validity;
  }

  /**
   * Set the hormonalTreatmentBeforeDurationValidity based on the received event value
   * @param validity The validity of the input
   */
   protected onHormonalTreatmentBeforeDurationValidityCheck(validity: boolean): void {
    this.hormonalTreatmentBeforeDurationValidity = validity;
  }

  protected onFamilyCancerUpdate(): void {
    if (this.questions.familyCancer) {
      Object.assign(this.questionnaire.questions, this.questions.familyCancer.toDto());
    }
  }

  protected onBreastSymptomsUpdate(): void {
    if (this.questions.breastSymptoms) {
      Object.assign(this.questionnaire.questions, this.questions.breastSymptoms.toDto());
    }
  }

  protected onBreastOperationsUpdate(): void {
    if (this.questions.breastOperations) {
      Object.assign(this.questionnaire.questions, this.questions.breastOperations.toDto());
    }
  }
}

